import {
    Button,
    DialogContent,
    DialogTitle,
    TextField,
    Box,
    Chip,
    IconButton,
    useTheme,
    useMediaQuery,
    Typography,
    Tooltip,
} from '@mui/material';
import { DefaultOpenModalProps } from 'components/button/OpenModalButton/OpenModalButton';
import React, { useEffect, useState } from 'react';
import { BaseModal } from '../BaseModal/BaseModal';
import { CreateVirtualDeviceModal } from './CreateVirtualDeviceModal/CreateVirtualDeviceModal';
import styles from './CreateDeviceModal.module.scss';
import { CreatePhysicalDeviceModal } from './CreatePhysicalDeviceModal/CreatePhysicalDeviceModal';
import { useLazyGetDeviceByLongIdNapiQuery } from 'store/services';
import { Device } from 'types';
import { Close, Error, HelpOutline } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { resetFormState } from 'store/features/form';

type DeviceType = 'Virtual' | 'Physical';

type modalState =
    | 'DeviceType'
    | 'DeployType'
    | 'CreatePhysical'
    | 'CreateVirtual'
    | 'FindOldDeviceId'
    | 'FindNewDeviceId';

// Create an object to make assigning the possible modal states easier.
const modalStates: {
    DeviceType: modalState;
    DeployType: modalState;
    CreatePhysical: modalState;
    CreateVirtual: modalState;
    FindOldDeviceId: modalState;
    FindNewDeviceId: modalState;
} = {
    DeviceType: 'DeviceType',
    DeployType: 'DeployType',
    CreatePhysical: 'CreatePhysical',
    CreateVirtual: 'CreateVirtual',
    FindOldDeviceId: 'FindOldDeviceId',
    FindNewDeviceId: 'FindNewDeviceId',
};

type DeviceButton = {
    type: DeviceType;
    tooltip: string;
};

interface CreateDeviceModalProps extends DefaultOpenModalProps {
    supplierId?: string;
    /** When device is added use set state callback to alert parent component to refetch devices. */
    onDeviceCreated?: React.Dispatch<React.SetStateAction<boolean>>;
    /** When device is added use set state callback to alert parent component what device id was created. */
    setCreatedDeviceId?: React.Dispatch<React.SetStateAction<string>>;
}

export function CreateDeviceModal({
    modalOpen,
    setModalOpen,
    supplierId,
    onDeviceCreated,
    setCreatedDeviceId,
}: CreateDeviceModalProps) {
    const theme = useTheme();
    const isTabletOrMobile = useMediaQuery(theme.breakpoints.down('lg'));
    const [deviceType, setDeviceType] = useState<DeviceType | null>(null);
    const [oldShortDeviceId, setOldShortDeviceId] = useState('');
    const [newShortDeviceId, setNewShortDeviceId] = useState('');
    const [newDevice, setNewDevice] = useState<Device | null>(null);
    const [oldDevice, setOldDevice] = useState<Device | null>(null);
    type deviceSearchErrorMessage =
        | null
        | 'Device id not found'
        | 'Device ids cannot be the same';
    const [deviceSearchError, setDeviceSearchError] = useState<{
        isErrorState: boolean;
        message: deviceSearchErrorMessage;
    }>({
        isErrorState: false,
        message: null,
    });

    const [queryDeviceById] = useLazyGetDeviceByLongIdNapiQuery();

    const [modalState, setModalState] = useState<modalState>(
        modalStates.DeviceType
    );

    const handleSetDeviceChange = (newDeviceType: DeviceType) => {
        setDeviceType(newDeviceType);
        setModalState(modalStates.DeployType);
    };

    const deviceTypes: DeviceButton[] = [
        {
            type: 'Physical',
            tooltip: 'Set up a new physical tank monitor on site.',
        },
        {
            type: 'Virtual',
            tooltip:
                'Add an account without a tank monitor to virtually forecast and plan deliveries.',
        },
    ];

    const handleDeviceSearch = async (
        shortDeviceId: string,
        deviceSearch: React.Dispatch<React.SetStateAction<Device | null>>,
        onSuccessCallback?: () => void
    ): Promise<void> => {
        // If shortDeviceId is the same as the old or new short device id, set an error
        if (oldShortDeviceId === newShortDeviceId) {
            setDeviceSearchError({
                isErrorState: true,
                message: 'Device ids cannot be the same',
            });
            return;
        }

        setDeviceSearchError({ isErrorState: false, message: null });

        const { data, isSuccess } = await queryDeviceById({
            longDeviceId: shortDeviceId,
        });
        if (isSuccess && data) {
            deviceSearch(data);
            onSuccessCallback && onSuccessCallback();
        } else {
            setDeviceSearchError({
                isErrorState: true,
                message: 'Device id not found',
            });
        }
    };
    const handleClose = () => {
        setModalOpen(false);
    };

    // handle certain init states for different modal states
    useEffect(() => {
        switch (modalState) {
            case modalStates.DeployType:
                setNewDevice(null);
                setOldDevice(null);
                setOldShortDeviceId('');
                setNewShortDeviceId('');
                break;
            default:
                break;
        }
    }, [modalState]);

    const handleBackButton = () => {
        switch (modalState) {
            case modalStates.DeployType:
                setModalState(modalStates.DeviceType);
                break;
            case modalStates.FindOldDeviceId:
                setModalState(modalStates.DeployType);
                break;
            case modalStates.FindNewDeviceId:
                setModalState(modalStates.DeployType);
                break;
            case modalStates.CreatePhysical:
                setModalState(modalStates.FindOldDeviceId);
                break;
            case modalStates.CreateVirtual:
                setModalState(modalStates.DeployType);
                break;
            default:
                break;
        }
    };

    const dispatch = useDispatch();
    useEffect(() => {
        if (!modalOpen) {
            setDeviceSearchError({ isErrorState: false, message: null });
            setDeviceType(null);
            setNewDevice(null);
            setOldDevice(null);
            setOldShortDeviceId('');
            setNewShortDeviceId('');
            setModalState(modalStates.DeviceType);
            dispatch(resetFormState());
        }
    }, [modalOpen, dispatch]);

    switch (modalState) {
        case modalStates.DeviceType:
            return (
                <BaseModal
                    data-testid="CreateDeviceModal"
                    open={modalOpen}
                    onClose={handleClose}
                    maxWidth="xs"
                >
                    <DialogTitle>Deploy Monitor</DialogTitle>
                    <IconButton
                        color="inherit"
                        className={styles.closeButton}
                        style={{ position: 'absolute' }}
                        onClick={() => setModalOpen(false)}
                    >
                        <Close />
                    </IconButton>
                    <DialogContent className={styles.modalContent}>
                        <Typography
                            variant="h6"
                            className={styles.modalContentHeader}
                            padding={2}
                            textAlign={'center'}
                        >
                            Select what type of monitor to deploy.
                        </Typography>
                        <Box
                            className={`${styles.typeButtons} ${
                                isTabletOrMobile ? styles.typeButtonsTablet : ''
                            }`}
                        >
                            {deviceTypes.map(({ type, tooltip }) => (
                                <Button
                                    variant="contained"
                                    size="large"
                                    key={type}
                                    onClick={() => handleSetDeviceChange(type)}
                                >
                                    {type}
                                    <Tooltip
                                        title={tooltip}
                                        enterTouchDelay={0}
                                        placement="bottom"
                                        className={
                                            styles.deviceTypeButtonTooltip
                                        }
                                    >
                                        <HelpOutline
                                            color="inherit"
                                            fontSize="small"
                                        />
                                    </Tooltip>
                                </Button>
                            ))}
                        </Box>
                    </DialogContent>
                </BaseModal>
            );
        case modalStates.DeployType:
            if (deviceType === 'Virtual') {
                setModalState(modalStates.CreateVirtual);
            }
            return (
                <BaseModal
                    open={modalOpen}
                    onClose={() => handleClose}
                    maxWidth="xs"
                >
                    <DialogTitle
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'flex-start',
                            alignItems: 'left',
                        }}
                    >
                        Deploy Monitor
                        <Button
                            variant="contained"
                            onClick={async () => {
                                handleBackButton();
                            }}
                            sx={{
                                width: '50px',
                                marginTop: '1rem',
                            }}
                        >
                            Back
                        </Button>
                    </DialogTitle>
                    <IconButton
                        color="inherit"
                        className={styles.closeButton}
                        style={{ position: 'absolute' }}
                        onClick={() => setModalOpen(false)}
                    >
                        <Close />
                    </IconButton>
                    <DialogContent className={styles.modalContent}>
                        <Typography
                            variant="h6"
                            className={styles.modalContentHeader}
                            textAlign={'center'}
                        >
                            Are you replacing a monitor?
                        </Typography>

                        <Box
                            className={`${styles.typeButtons} ${
                                isTabletOrMobile ? styles.typeButtonsTablet : ''
                            }`}
                            padding={2}
                        >
                            <Button
                                variant="contained"
                                size="large"
                                onClick={() => {
                                    setModalState(modalStates.FindOldDeviceId);
                                }}
                            >
                                Yes
                            </Button>
                            <Button
                                variant="contained"
                                size="large"
                                onClick={() => {
                                    // If physical device, go to find new device id
                                    if (deviceType === 'Physical') {
                                        setModalState(
                                            modalStates.FindNewDeviceId
                                        );
                                    } else {
                                        setModalState(
                                            modalStates.CreateVirtual
                                        );
                                    }
                                }}
                            >
                                No
                            </Button>
                        </Box>
                    </DialogContent>
                </BaseModal>
            );
        case modalStates.FindOldDeviceId:
            return (
                <BaseModal
                    open={modalOpen}
                    onClose={() => handleClose}
                    maxWidth="xs"
                >
                    <DialogTitle
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'flex-start',
                            alignItems: 'left',
                        }}
                    >
                        Deploy Monitor
                        <Button
                            variant="contained"
                            onClick={async () => {
                                handleBackButton();
                            }}
                            sx={{
                                width: '50px',
                                marginTop: '1rem',
                            }}
                        >
                            Back
                        </Button>
                    </DialogTitle>
                    <IconButton
                        color="inherit"
                        className={styles.closeButton}
                        style={{ position: 'absolute' }}
                        onClick={() => setModalOpen(false)}
                    >
                        <Close />
                    </IconButton>
                    <DialogContent className={styles.modalContent}>
                        {deviceType === 'Physical' && (
                            <Box>
                                <Typography
                                    variant="h6"
                                    className={styles.modalContentHeader}
                                    textAlign={'center'}
                                >
                                    Enter old device id.
                                </Typography>
                                <Box className={styles.physicalSearchContainer}>
                                    <Box className={styles.inputContainer}>
                                        <Box
                                            className={
                                                styles.searchInputContainer
                                            }
                                        >
                                            <TextField
                                                id="physical-device-search-input"
                                                variant="standard"
                                                label="Device ID:"
                                                value={oldShortDeviceId}
                                                onChange={(
                                                    e: React.ChangeEvent<HTMLInputElement>
                                                ) =>
                                                    setOldShortDeviceId(
                                                        e.target.value
                                                    )
                                                }
                                            />
                                            <Tooltip
                                                title="device Id is located on the bottom of the monitor"
                                                enterTouchDelay={0}
                                                placement="bottom"
                                            >
                                                <HelpOutline
                                                    color="action"
                                                    fontSize="small"
                                                />
                                            </Tooltip>
                                        </Box>

                                        <Button
                                            variant="contained"
                                            onClick={async () => {
                                                await handleDeviceSearch(
                                                    oldShortDeviceId,
                                                    setOldDevice,
                                                    () => {
                                                        setModalState(
                                                            modalStates.FindNewDeviceId
                                                        );
                                                    }
                                                );
                                            }}
                                        >
                                            Find
                                        </Button>
                                    </Box>

                                    {deviceSearchError.isErrorState && (
                                        <Chip
                                            icon={<Error />}
                                            label={deviceSearchError.message}
                                            color="error"
                                        />
                                    )}
                                </Box>
                            </Box>
                        )}
                    </DialogContent>
                </BaseModal>
            );
        case modalStates.FindNewDeviceId:
            return (
                <BaseModal
                    open={modalOpen}
                    onClose={() => handleClose}
                    maxWidth="xs"
                >
                    <DialogTitle
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'flex-start',
                            alignItems: 'left',
                        }}
                    >
                        Deploy Monitor
                        <Button
                            variant="contained"
                            onClick={async () => {
                                handleBackButton();
                            }}
                            sx={{
                                width: '50px',
                                marginTop: '1rem',
                            }}
                        >
                            Back
                        </Button>
                    </DialogTitle>
                    <IconButton
                        color="inherit"
                        className={styles.closeButton}
                        style={{ position: 'absolute' }}
                        onClick={() => setModalOpen(false)}
                    >
                        <Close />
                    </IconButton>
                    <DialogContent className={styles.modalContent}>
                        {deviceType === 'Physical' && (
                            <Box>
                                <Typography
                                    variant="h6"
                                    className={styles.modalContentHeader}
                                    textAlign={'center'}
                                >
                                    Enter new device id.
                                </Typography>
                                <Box className={styles.physicalSearchContainer}>
                                    <Box className={styles.inputContainer}>
                                        <Box
                                            className={
                                                styles.searchInputContainer
                                            }
                                        >
                                            <TextField
                                                id="physical-device-search-input"
                                                variant="standard"
                                                label="Device ID:"
                                                value={newShortDeviceId}
                                                onChange={(
                                                    e: React.ChangeEvent<HTMLInputElement>
                                                ) =>
                                                    setNewShortDeviceId(
                                                        e.target.value
                                                    )
                                                }
                                            />
                                            <Tooltip
                                                title="device Id is located on the bottom of the monitor"
                                                enterTouchDelay={0}
                                                placement="bottom"
                                            >
                                                <HelpOutline
                                                    color="action"
                                                    fontSize="small"
                                                />
                                            </Tooltip>
                                        </Box>

                                        <Button
                                            variant="contained"
                                            onClick={async () => {
                                                await handleDeviceSearch(
                                                    newShortDeviceId,
                                                    setNewDevice,
                                                    () => {
                                                        if (
                                                            deviceType ===
                                                            'Physical'
                                                        ) {
                                                            setModalState(
                                                                modalStates.CreatePhysical
                                                            );
                                                        } else {
                                                            setModalState(
                                                                modalStates.CreateVirtual
                                                            );
                                                        }
                                                    }
                                                );
                                            }}
                                        >
                                            Find
                                        </Button>
                                    </Box>

                                    {deviceSearchError.isErrorState && (
                                        <Chip
                                            icon={<Error />}
                                            label={deviceSearchError.message}
                                            color="error"
                                        />
                                    )}
                                </Box>
                            </Box>
                        )}
                    </DialogContent>
                </BaseModal>
            );

        case modalStates.CreatePhysical:
            return (
                <CreatePhysicalDeviceModal
                    modalOpen={modalOpen}
                    setModalOpen={setModalOpen}
                    newDevice={newDevice}
                    oldDevice={oldDevice}
                    supplierId={supplierId}
                    onDeviceCreated={onDeviceCreated}
                    setCreatedDeviceId={setCreatedDeviceId}
                />
            );
        case modalStates.CreateVirtual:
            return (
                <CreateVirtualDeviceModal
                    modalOpen={modalOpen}
                    setModalOpen={setModalOpen}
                    supplierId={supplierId}
                    onDeviceCreated={onDeviceCreated}
                    setCreatedDeviceId={setCreatedDeviceId}
                />
            );
        default:
            return <Box></Box>;
    }
}
